import { useMemo, useState, useEffect, useCallback, useRef } from 'react'

import { useLocation, useParams } from 'react-router-dom'
import { v4 as makeUuid } from 'uuid'
import MD5 from 'crypto-js/md5'

import { useFormatMessage } from 'lib/localization'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isSuccess } from 'redux/toolkit/api'
import {
  getAccountSettings,
  getDomainSettings,
  setHasPageChanges,
  updateAccountSettings,
  updateDomainSettings,
  reset
} from 'redux/features/settings/settingsSlice'
import { setErrorSnackBar, setSuccessSnackBar } from 'redux/features/app/appSlice'
import { AvailableSettings, BulkEmailExemptPolicy, SettingValue, SettingsObject } from 'types/Settings'
import { useApi } from 'v2/modules/lib/hooks/useApi'
import appConfig from 'config/appConfig'
import { BULK_EDIT_CONFIG } from './config'
import { classifyBulkEmailExemptItem } from './adminBulkEditUtils'

export interface State {
  title: string
  rawData: string
  forceStatus?: 'loading' | 'success' | 'failed' | 'saving' | 'saveComplete'
}

export interface EventHandlers {
  onCancelConfirm: () => void
  onSave: () => void
  onChange: (e: React.FormEvent<HTMLTextAreaElement>) => void
}

export type UseAdminBulkEditLogic = [State, EventHandlers]

const BASE_I18N_KEY = 'ess.settings.bulk_edit'

export const useAdminBulkEditLogic = (): UseAdminBulkEditLogic => {
  const { domainId } = useParams()
  const { pathname } = useLocation()
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const {
    accessTokenObject,
    accessToken,
    isUpdateAccountSettingsSuccess,
    isUpdateDomainSettingsSuccess,
    accountSettings,
    domainSettings,
    hasPageChanges
  } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    accessToken: _stores.auth.accessToken,
    isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateDomainSettingsSuccess: isSuccess(_stores.settings.updateDomainSettingsApiStatus),
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings,
    hasPageChanges: _stores.settings.hasPageChanges
  }))

  const api = useApi({
    host: appConfig.GATEWAY_URL ? `${appConfig.GATEWAY_URL}/v2` : '',
    accessToken: accessToken || ''
  })
  const [rawData, setRawData] = useState('')
  const [initialRawData, setInitialRawData] = useState('')
  const [forceStatus, setForceStatus] = useState<'loading' | 'success' | 'failed' | 'saving' | 'saveComplete'>()

  const currentDomainId = useMemo(() => domainId || accessTokenObject?.pdDomainId, [accessTokenObject, domainId])
  const config = useMemo(() => {
    const path = domainId ? pathname.replace(domainId, ':domainId') : pathname
    return BULK_EDIT_CONFIG()[path]
  }, [pathname, domainId])

  const defaultLoadStrategy = useCallback(() => {
    if (currentDomainId) {
      dispatch(getDomainSettings({ domainId: currentDomainId, settings: config.settings }))
    } else {
      dispatch(getAccountSettings(config.settings))
    }
  }, [dispatch, currentDomainId, config.settings])

  const bulkEmailExemptLoadStrategy = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      if (accessTokenObject?.accountId === undefined) {
        return
      }
      setForceStatus('loading')
      try {
        const settings = await api.accountService.getAccountSettings(accessTokenObject.accountId, [
          AvailableSettings.BULK_EMAIL_SENDER_EXEMPT,
          AvailableSettings.BULK_EMAIL_RCPT_EXEMPT
        ])
        const data: BulkEmailExemptPolicy[] = []
        settings.forEach(setting => {
          if (Array.isArray(setting.value)) {
            setting.value.forEach(item => {
              data.push({
                ...item,
                type: setting.variable as
                  | AvailableSettings.BULK_EMAIL_SENDER_EXEMPT
                  | AvailableSettings.BULK_EMAIL_RCPT_EXEMPT
              })
            })
          }
        })
        const localRawData = config.dataToCsv(JSON.stringify(data), formatMessage, accessTokenObject?.isCplAccount)
        setRawData(localRawData)
        setInitialRawData(localRawData)
        setForceStatus('success')
      } catch (error) {
        setForceStatus('failed')
      }
    })()
  }, [accessTokenObject?.accountId, accessTokenObject?.isCplAccount, api.accountService, config, formatMessage])

  // init
  useEffect(() => {
    if (config.strategy === 'bulk_email_exempt_strategy') {
      bulkEmailExemptLoadStrategy()
      return
    }
    defaultLoadStrategy()
    // eslint-disable-next-line
  }, [])

  // unmount
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch]
  )

  useEffect(() => {
    if (isUpdateAccountSettingsSuccess || isUpdateDomainSettingsSuccess || forceStatus === 'saveComplete') {
      if (config.backUrl.id === 'EDIT_DOMAIN' && domainId) {
        config.backUrl.goto({ domainId })
      } else {
        config.backUrl.goto()
      }
    }
  }, [isUpdateAccountSettingsSuccess, isUpdateDomainSettingsSuccess, config, domainId, forceStatus])

  useEffect(() => {
    if (config.strategy === 'bulk_email_exempt_strategy') {
      return
    }
    const settings = currentDomainId ? domainSettings : accountSettings
    let settingName = config.settings[0]
    if (config.dependingSetting) {
      settingName = config.dependingOutcome(settings[config.dependingSetting] as SettingValue)
    }

    // Forbid accessing the bulk edit page which is not used according to the INBOUND_TLS_ALL setting value
    if (config.settings.includes(AvailableSettings.INBOUND_TLS_ALL) && settings.inbound_tls_all !== null) {
      if (
        settings.inbound_tls_all === SettingValue.ENABLED &&
        config.settings.includes(AvailableSettings.INBOUND_TLS_DOMAINS)
      ) {
        config.backUrl.goto()
        return
      }
      if (
        settings.inbound_tls_all !== SettingValue.ENABLED &&
        config.settings.includes(AvailableSettings.INBOUND_TLS_DOMAIN_EXEMPTIONS)
      ) {
        config.backUrl.goto()
        return
      }
    }

    const data = settings[settingName]
    try {
      setRawData(config.dataToCsv(data as string, formatMessage, accessTokenObject?.isCplAccount))
      setInitialRawData(config.dataToCsv(data as string, formatMessage, accessTokenObject?.isCplAccount))
    } catch (error) {
      dispatch(
        setErrorSnackBar({
          message: 'error_parsing_settings'
        })
      )
    }
  }, [dispatch, formatMessage, setRawData, config, accessTokenObject, domainSettings, accountSettings, currentDomainId])

  const updateSettings = useCallback(
    (settings: SettingsObject) => {
      if (currentDomainId) {
        dispatch(
          updateDomainSettings({
            domainId: currentDomainId,
            settings
          })
        )
      } else {
        dispatch(updateAccountSettings({ settings }))
      }
    },
    [dispatch, currentDomainId]
  )

  const onCancelConfirm = useCallback(() => {
    if (hasPageChanges) {
      setRawData(initialRawData)
      dispatch(setHasPageChanges(false))
    } else if (config.backUrl.id === 'EDIT_DOMAIN' && domainId) {
      config.backUrl.goto({ domainId })
    } else {
      config.backUrl.goto()
    }
  }, [dispatch, config, hasPageChanges, initialRawData, domainId])

  const onSaveDefault = useCallback(() => {
    try {
      const settings = currentDomainId ? domainSettings : accountSettings
      const settingName = config.dependingSetting
        ? config.dependingOutcome(settings[config.dependingSetting] as SettingValue)
        : config.settings[0]
      // Pass existing data to rawDataToArray so it can preserve IDs for existing entries
      // The 4th parameter is optional and only used by configs that support ID preservation
      const existingData = settings[settingName]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawDataToArrayFn = config.rawDataToArray as (r: string, f: any, c?: boolean, e?: any) => any[]
      const data = rawDataToArrayFn(rawData, formatMessage, accessTokenObject?.isCplAccount, existingData)
      updateSettings({ [settingName]: data })
      dispatch(setHasPageChanges(false))
    } catch (err) {
      dispatch(
        setErrorSnackBar({
          message: err.message
        })
      )
    }
  }, [
    formatMessage,
    dispatch,
    updateSettings,
    accessTokenObject,
    config,
    rawData,
    domainSettings,
    accountSettings,
    currentDomainId
  ])

  const getExistingSettings = useCallback(
    async (accountId: string) => {
      const existingSettings = await api.accountService.getAccountSettings(accountId, [
        AvailableSettings.BULK_EMAIL_SENDER_EXEMPT,
        AvailableSettings.BULK_EMAIL_RCPT_EXEMPT
      ])
      const existingSenderMap = new Map<string, Record<string, any>>()
      const existingRcptMap = new Map<string, Record<string, any>>()

      existingSettings.forEach(setting => {
        if (Array.isArray(setting.value)) {
          setting.value.forEach((item: Record<string, any>) => {
            if (setting.variable === AvailableSettings.BULK_EMAIL_SENDER_EXEMPT) {
              // toLowerCase to make sure comparing db value to user input value accurately
              existingSenderMap.set(item.name.toLowerCase(), item)
            } else if (setting.variable === AvailableSettings.BULK_EMAIL_RCPT_EXEMPT) {
              existingRcptMap.set(item.name.toLowerCase(), item)
            }
          })
        }
      })
      return { existingSenderMap, existingRcptMap }
    },
    [api.accountService]
  )

  const classifyBulkEmailExemptValues = useCallback(
    (
      data: BulkEmailExemptPolicy[],
      existingSenderMap: Map<string, Record<string, any>>,
      existingRcptMap: Map<string, Record<string, any>>
    ) => {
      const modifiedTimestamp = Math.round(new Date().getTime() / 1000)

      const newSenderItems: Record<string, any>[] = []
      const newRcptItems: Record<string, any>[] = []
      const unchangedSenderItems: Record<string, any>[] = []
      const unchangedRcptItems: Record<string, any>[] = []
      const modifiedSenderItems: Record<string, any>[] = []
      const modifiedRcptItems: Record<string, any>[] = []

      data.forEach((item: BulkEmailExemptPolicy) => {
        const domainOrEmail = item.name.toLowerCase()
        if (item.type === AvailableSettings.BULK_EMAIL_SENDER_EXEMPT) {
          const existing = existingSenderMap.get(domainOrEmail)
          const classificationResult = classifyBulkEmailExemptItem(item, existing)

          switch (classificationResult.classification) {
            case 'NEW': {
              const { type, ...rest } = classificationResult.item
              newSenderItems.push({ ...rest, id: MD5(makeUuid()).toString(), modified: modifiedTimestamp })
              break
            }
            case 'MODIFIED': {
              modifiedSenderItems.push({
                ...classificationResult.existing,
                comment: classificationResult.item.comment,
                modified: modifiedTimestamp
              })
              break
            }
            case 'UNCHANGED': {
              unchangedSenderItems.push(classificationResult.existing)
              break
            }
            default:
              // No action needed for unknown classification
              break
          }
          existingSenderMap.delete(domainOrEmail)
        } else if (item.type === AvailableSettings.BULK_EMAIL_RCPT_EXEMPT) {
          const existing = existingRcptMap.get(domainOrEmail)
          const classificationResult = classifyBulkEmailExemptItem(item, existing)

          switch (classificationResult.classification) {
            case 'NEW': {
              const { type, ...rest } = classificationResult.item
              newRcptItems.push({ ...rest, id: MD5(makeUuid()).toString(), modified: modifiedTimestamp })
              break
            }
            case 'MODIFIED': {
              modifiedRcptItems.push({
                ...classificationResult.existing,
                comment: classificationResult.item.comment,
                modified: modifiedTimestamp
              })
              break
            }
            case 'UNCHANGED': {
              unchangedRcptItems.push(classificationResult.existing)
              break
            }
            default:
              // No action needed for unknown classification
              break
          }
          existingRcptMap.delete(domainOrEmail)
        }
      })

      return {
        newSenderItems,
        newRcptItems,
        unchangedSenderItems,
        unchangedRcptItems,
        modifiedSenderItems,
        modifiedRcptItems
      }
    },
    []
  )

  const onSaveBulkEmailExempt = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      try {
        if (!accessTokenObject?.accountId) {
          return
        }
        setForceStatus('saving')
        // 1. fetch existing settings
        const { existingSenderMap, existingRcptMap } = await getExistingSettings(accessTokenObject.accountId)

        // 2. grab rawData
        const data = config.rawDataToArray(
          rawData,
          formatMessage,
          accessTokenObject?.isCplAccount
        ) as BulkEmailExemptPolicy[]

        // 3. classify NEW, MODIFIED, UNCHANGED items from each settings
        const {
          newSenderItems,
          newRcptItems,
          unchangedSenderItems,
          unchangedRcptItems,
          modifiedSenderItems,
          modifiedRcptItems
        } = classifyBulkEmailExemptValues(data, existingSenderMap, existingRcptMap)

        // 4. combine items as final item for each settings
        const finalSenderItems = [...newSenderItems, ...modifiedSenderItems, ...unchangedSenderItems]
        const finalRcptItems = [...newRcptItems, ...modifiedRcptItems, ...unchangedRcptItems]

        // 5. save settings
        await api.accountService.saveAccountSettings(accessTokenObject.accountId, [
          {
            variable: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT,
            value: finalSenderItems
          },
          {
            variable: AvailableSettings.BULK_EMAIL_RCPT_EXEMPT,
            value: finalRcptItems
          }
        ])
        dispatch(setHasPageChanges(false))
        dispatch(
          setSuccessSnackBar({
            message: 'success_changing_settings'
          })
        )
        setForceStatus('saveComplete')
      } catch (err) {
        const errorMsg =
          typeof err?.message === 'string' && err.message.startsWith('@literal:')
            ? err.message
            : 'error_changing_settings'
        dispatch(
          setErrorSnackBar({
            message: errorMsg
          })
        )
        // (this does not mean "save success") ---> set forceStatus as "success" for SettingsPage component to return to the loaded (editable) state
        setForceStatus('success')
      }
    })()
  }, [
    accessTokenObject?.accountId,
    accessTokenObject?.isCplAccount,
    getExistingSettings,
    config,
    rawData,
    formatMessage,
    classifyBulkEmailExemptValues,
    api.accountService,
    dispatch
  ])

  const onSave = useCallback(() => {
    if (config.strategy === 'bulk_email_exempt_strategy') {
      onSaveBulkEmailExempt()
      return
    }
    onSaveDefault()
  }, [config, onSaveDefault, onSaveBulkEmailExempt])

  const onChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      setRawData(e.currentTarget.value)
      dispatch(setHasPageChanges(true))
    },
    [dispatch]
  )

  return useMemo(
    () => [
      {
        title: config.title,
        rawData,
        forceStatus
      },
      {
        onCancelConfirm,
        onSave,
        onChange
      }
    ],
    [config, rawData, forceStatus, onCancelConfirm, onSave, onChange]
  )
}
